import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-1a404df9"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 1 }

import { reactive, ref, computed, watch, PropType } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import {
  EquipmentViewService,
  RequestParams as EquipmentRequestParams,
  getPeripheralTypeLabel,
  PERIPHERAL_TYPE_VALUES,
} from '@/views/dashboard/data-safe/usb-manage/usb-statistics/_components/equipment-view/equipment-view.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { FormSize } from '@/components/cmForm/types'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { listCol } from '@/utils/list-columns-i18n'
import TerminalList from '@/views/dashboard/data-safe/usb-manage/usb-statistics/_components/equipment-view/_components/terminal-list.vue'

type ExcludeEquipmentFilterKeys = Extract<
  keyof EquipmentRequestParams,
  'search_word' | 'limit' | 'page' | 'sort_key' | 'asc' | 'time_range' | 'peripheral_type'
>


export default /*#__PURE__*/_defineComponent({
  __name: 'equipment-list',
  props: {
  /** 设备类型筛选，与概览 peripheral_type 一致；会随列表请求传给 /peripheralcontrol/view/peripheral/get */
  initialPeripheralType: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
  hasPermission: {
    type: Boolean,
    default: true,
  },
  /** 抽屉可见性，用于避免关闭时参数清理触发二次请求 */
  visible: {
    type: Boolean,
    default: true,
  },
},
  setup(__props) {

const props = __props

const { t, locale } = useI18n()

const injector = useReflectiveInjector([EquipmentViewService])
const equipmentViewService = injector.get(EquipmentViewService)
const dataApi = equipmentViewService.getPeripheralcontrolViewTerminalGet.bind(equipmentViewService)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)

function resolvePeripheralType (): number | undefined {
  const v = props.initialPeripheralType
  if (v === undefined || v === null) return undefined
  return Number(v)
}

const queryParams = reactive<Partial<EquipmentRequestParams>>({
  page: 1,
  limit: 20,
  search_word: '',
  sort_key: '',
  asc: false,
  time_range: 1,
  peripheral_type: resolvePeripheralType(),
})

watch(
  () => props.initialPeripheralType,
  () => {
    queryParams.peripheral_type = resolvePeripheralType()
    if (!props.visible) return
    void refreshList()
  }
)

const filterFormColumns = computed<ObjectFormItem<EquipmentRequestParams, ExcludeEquipmentFilterKeys>>(() => {
  void locale.value
  return {
    peripheral_type: {
      colOpt: { style: 'flex: 0 0 130px' },
      label: t('usbPolicy.deviceType'),
      type: FormType.Select,
      outSearchFiledFlag: true,
      dictData: [
        { label: t('usbPolicy.all'), value: undefined },
        ...PERIPHERAL_TYPE_VALUES.map((value) => ({
          label: getPeripheralTypeLabel(value, t),
          value,
        })),
      ],
      events: {
        change: () => { void refreshList() },
      },
    },
    time_range: {
      colOpt: { style: 'flex: 0 0 130px' },
      label: t('usbPolicy.recordTime'),
      type: FormType.Select,
      outSearchFiledFlag: true,
      dictData: [
        { label: t('usbPolicy.all'), value: 1 },
        { label: t('usbPolicy.last24h'), value: 2 },
        { label: t('usbPolicy.last7d'), value: 3 },
        { label: t('usbPolicy.lastMonth'), value: 4 },
      ],
      events: {
        change: () => {
          void refreshList()
        },
      }
    },
  } as unknown as ObjectFormItem<EquipmentRequestParams, ExcludeEquipmentFilterKeys>
})

const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})
const terminalDrawerVisible = ref(false)
const currentPeripheralId = ref('')

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: () => [
      {
        name: listCol(t, 'dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    height: '100%',
    path: 'list',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'peripheral_id',
    selection: false,
    bodyStyle: 'padding:10px 16px',
    showTip: false,
    usePagination: false,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      { label: listCol(t, 'deviceId'), prop: 'peripheral_id', columnOpt: { minWidth: 120, sortable: false }, showOverflow: true },
      { label: listCol(t, 'deviceType'), prop: 'peripheral_type', columnOpt: { width: 100, sortable: false } },
      { label: listCol(t, 'deviceName'), prop: 'peripheral_name', columnOpt: { minWidth: 120, sortable: false } },
      {
        label: t('usbPolicy.accessedTerminalCount'),
        prop: 'access_device_count',
        columnOpt: { width: 130, sortable: false, align: 'left' },
      },
    ],
  }
})

async function handleSearch(value: string) {
  queryParams.search_word = value
  await refreshList()
}

async function handleQuery(form: Partial<EquipmentRequestParams>) {
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.search_word = ''
  queryParams.time_range = 1
  queryParams.peripheral_type = resolvePeripheralType()
  void refreshList()
}

function openTerminalDrawer(row: Record<string, unknown>) {
  currentPeripheralId.value = String(row.peripheral_id || '')
  if (!currentPeripheralId.value) return
  terminalDrawerVisible.value = true
}

function handleAction({ type, rowItem }: { type: string; rowItem?: Record<string, unknown> }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<EquipmentRequestParams> = {
    search_word: queryParams.search_word,
    time_range: queryParams.time_range,
    sort_key: queryParams.sort_key,
    asc: queryParams.asc,
    peripheral_type: queryParams.peripheral_type,
  }
  equipmentViewService.download(type, params, t('usbPolicy.exportEquipmentFileName'))
}

/** 首屏仅 CmTable 默认 firstLoading；`initialPeripheralType` 变更仍由 watch 触发 refresh，避免与原 onMounted 双发 */

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_drawer = _resolveComponent("el-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      class: "table-part equipment-list-table",
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": props.hasPermission,
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          disabled: !props.hasPermission,
          "show-search": true,
          "search-placeholder": _ctx.$t('usbPolicy.searchPlaceholder'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(ExportDropdown, {
              "has-permission": props.hasPermission,
              "csv-i18n-key": "usbPolicy.exportCsv",
              "excel-i18n-key": "usbPolicy.exportExcel",
              onExport: download
            }, null, 8, ["has-permission"])
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"])
      ]),
      peripheral_type: _withCtx(({ value }: { value?: number }) => [
        _createElementVNode("span", null, _toDisplayString(value != null ? (_unref(getPeripheralTypeLabel)(value, _unref(t)) || '--') : '--'), 1)
      ]),
      access_device_count: _withCtx(({ row, value }: { row?: Record<string, unknown>; value?: number }) => [
        (row && Number(value) > 0)
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "text",
              size: "small",
              onClick: ($event: any) => (openTerminalDrawer(row))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(value), 1)
              ]),
              _: 2
            }, 1032, ["onClick"]))
          : (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(value != null ? value : (row ? 0 : '--')), 1))
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"]),
    _createVNode(_component_el_drawer, {
      size: "70%",
      direction: "rtl",
      modelValue: terminalDrawerVisible.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((terminalDrawerVisible).value = $event)),
      "destroy-on-close": "",
      title: _unref(t)('usbPolicy.accessedTerminalListTitle', { id: currentPeripheralId.value || '--' })
    }, {
      default: _withCtx(() => [
        (terminalDrawerVisible.value && currentPeripheralId.value)
          ? (_openBlock(), _createBlock(TerminalList, {
              key: 0,
              "peripheral-id": currentPeripheralId.value,
              "time-range": Number(queryParams.time_range) || 1
            }, null, 8, ["peripheral-id", "time-range"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})